import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface RouteProps {
  children: JSX.Element;
}

export function PrivateRoute({ children }: RouteProps): JSX.Element {
  const { user } = useAuth();
  const { state } = useLocation();

  if (!user) {
    return <Navigate to="/signin" state={state} />;
  }

  return children;
}

export function AdminRoute({ children }: RouteProps): JSX.Element {
  const { user, hasPermission } = useAuth();
  const { state } = useLocation();

  if (!user) {
    return <Navigate to="/signin" state={state} />;
  }

  // Mantém compatibilidade com isAdmin ou verifica permissões admin
  if (!user.isAdmin && !hasPermission(['dashboard:view', 'users:create', 'profiles:create'])) {
    return <Navigate to="/tech-visit-reports" state={state} />;
  }

  return children;
}

interface PermissionRouteProps {
  children: JSX.Element;
  permission?: string | string[];
  allPermissions?: string[];
  allowAdmin?: boolean;
}

/**
 * Rota protegida por permissões
 * @param permission - Permissão(ões) necessária(s) - usuário precisa ter pelo menos uma (OR)
 * @param allPermissions - Todas as permissões necessárias (AND)
 * @param allowAdmin - Se true, permite acesso para isAdmin mesmo sem a permissão
 */
export function PermissionRoute({
  children,
  permission,
  allPermissions,
  allowAdmin = false
}: PermissionRouteProps): JSX.Element {
  const { user, hasPermission, hasAllPermissions } = useAuth();
  const { state } = useLocation();

  if (!user) {
    return <Navigate to="/signin" state={state} />;
  }

  // Se allowAdmin e é admin, permite acesso
  if (allowAdmin && user.isAdmin) {
    return children;
  }

  // Verifica permissões específicas
  let hasAccess = false;

  if (allPermissions && allPermissions.length > 0) {
    hasAccess = hasAllPermissions(allPermissions);
  } else if (permission) {
    hasAccess = hasPermission(permission);
  } else {
    // Se não especificou permissões, permite (rota privada básica)
    hasAccess = true;
  }

  if (!hasAccess) {
    return <Navigate to="/tech-visit-reports" state={state} />;
  }

  return children;
}
